import { useMemo, useState, useCallback } from 'react';
import { Badge, Card, Modal, type BadgeTone } from '@safetyhub/ui';
import { useNavigate } from 'react-router-dom';
import {
  InspectionListItem,
  InspectionStatus,
} from '../../../lib/inspections';
import { filterInspectionsToMonth } from '../lib/monthStats';
import { ConfirmDialog } from '../../../components/inspection/shared/ConfirmDialog';
import { PortalTooltip } from '../../../components/inspection/desktop/PortalTooltip';
import { ChevronRightIcon, PlusIcon } from '../../../components/icons';
import { MODAL_LAYER } from '../../../components/modalLayers';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuthStore } from '../../../stores/auth';
import { canModifyInspectionRow } from '../../../utils/inspectionRowAccess';
import { getEvidenceDisplayName, REPORT_STATUS_LABELS } from '@safetyhub/shared';
import { getReportTypeLabel } from '../../../lib/reports';
import { resolveReportStatusLabel } from '../../../lib/reportStatusLabel';
import { isTbmEducationDocument } from '../../reports/postSubmitFlows';

interface DayInspectionListProps {
  date: string | null; // "YYYY-MM-DD"
  inspections: InspectionListItem[];
  allInspections?: InspectionListItem[]; // 월 요약용 전체 목록
  currentMonth?: { year: number; month: number }; // 0-indexed month
  onCreateInspection: (date: string) => void;
  onDeleteInspection: (id: string) => Promise<void>;
  onStartScheduled?: (id: string) => void;
  onCreateReport?: (id: string) => void;
}

/**
 * 상태 라벨의 **간결체** — 일정 목록 배지는 폭이 좁아 공백 없는 어휘를 쓴다.
 *
 * ⚠️ 종전엔 이 맵이 라벨 정본을 통째로 덮어썼다. 그러면 `resolveReportStatusLabel`의 **데이터
 * 판정**(교육이 도는 DRAFT = '진행 중' / 결재 없는 완료본 = '완료')이 상태 enum 한 칸으로
 * 뭉개진다. 그래서 정본을 먼저 부르고, **데이터 판정이 발동하지 않아 결과가 정본 라벨과 같을
 * 때만** 간결체를 덧입힌다(`compactStatusLabel`). 조건부인 이유가 이것이니, 무조건 간결체로
 * 되돌리지 말 것.
 */
const COMPACT_STATUS_LABEL: Record<string, string> = {
  CREATING: '생성대기',
  DRAFT: '작성중',
  PENDING_APPROVAL: '결재대기',
  REJECTED: '반려',
  CANCELLED: '취소',
};

/** 위 규칙의 실행부 — 정본 라벨과 같을 때만(=데이터 판정 미발동) 폭을 줄인다. */
function compactStatusLabel(status: string, resolved: string): string {
  const canonical = (REPORT_STATUS_LABELS as Record<string, string>)[status];
  if (resolved !== canonical) return resolved;
  return COMPACT_STATUS_LABEL[status] ?? resolved;
}

// 보고서 상태별 배지 색상 — 목업 세만틱 5색(blue/emerald/amber/rose/violet)에 수렴시키므로
// 기존 green→emerald, yellow→amber, red→rose, gray→slate. 상태→색 대응 자체는 불변.
const REPORT_STATUS_BADGE_TONE: Record<string, BadgeTone> = {
  APPROVED: 'emerald',
  DRAFT: 'slate',
  CREATING: 'slate',
  PENDING_APPROVAL: 'amber',
  REJECTED: 'rose',
  CANCELLED: 'slate',
};

// 상태별 좌측 색상 바
const STATUS_BAR_COLOR: Record<string, string> = {
  [InspectionStatus.SCHEDULED]: 'bg-violet-500',
  [InspectionStatus.IN_PROGRESS]: 'bg-blue-500',
  [InspectionStatus.COMPLETED]: 'bg-emerald-500',
  [InspectionStatus.PENDING_REVIEW]: 'bg-amber-500',
};

const STATUS_LABEL: Record<string, string> = {
  [InspectionStatus.SCHEDULED]: '예약됨',
  [InspectionStatus.IN_PROGRESS]: '진행 중',
  [InspectionStatus.COMPLETED]: '완료',
  [InspectionStatus.PENDING_REVIEW]: '검토 대기',
};

/**
 * 상태 배지 tone — 목업 `.evc-st`(연배경 필 + 점). 좌측 색 바(`STATUS_BAR_COLOR`)와 같은
 * 계열이라 한쪽만 바꾸면 카드 안에서 색이 갈린다. 맨 텍스트가 아니라 필을 쓰는 이유는
 * 카드 제목과 같은 줄에 놓여 굵은 글씨끼리 경합하기 때문이다.
 */
const STATUS_BADGE_TONE: Record<string, BadgeTone> = {
  [InspectionStatus.SCHEDULED]: 'violet',
  [InspectionStatus.IN_PROGRESS]: 'blue',
  [InspectionStatus.COMPLETED]: 'emerald',
  [InspectionStatus.PENDING_REVIEW]: 'amber',
};

function formatTime(isoString?: string): string {
  if (!isoString) return '';
  const d = new Date(isoString);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const period = h < 12 ? '오전' : '오후';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${period} ${displayH}:${m}`;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/** 패널 제목 — 목업 `.sp-h .d1`(`6월 10일`). */
function formatDayLabel(dateKey: string): string {
  const [, mo, d] = dateKey.split('-');
  return `${parseInt(mo)}월 ${parseInt(d)}일`;
}

/**
 * 제목 아래 요일 — 목업 `.sp-h .d2`(`수요일`).
 * 날짜와 한 줄에 묶여 있던 `(수)`를 아래로 내린 것: 제목은 날짜만 크게 읽히는 게 낫고,
 * 요일·건수는 같은 층의 보조 정보라 한 줄에 모이는 게 맞다.
 */
function formatWeekdayLabel(dateKey: string): string {
  const [y, mo, d] = dateKey.split('-');
  const date = new Date(parseInt(y), parseInt(mo) - 1, parseInt(d));
  return `${WEEKDAYS[date.getDay()]}요일`;
}

/**
 * 카드 인라인 액션 버튼 (목업 `.evc-actions button`).
 *
 * 비활성일 때 `disabled` 속성 대신 `aria-disabled`를 쓴다 — 크롬은 disabled 컨트롤 위에서
 * 마우스 이벤트를 아예 보내지 않아 사유 툴팁이 뜨지 않는다(액션 모달도 같은 이유로 aria만 쓴다).
 * 클릭은 활성/비활성과 무관하게 전파를 막는다: 카드 클릭은 액션 모달을 여는 별개 동작이다.
 */
function InlineActionButton({
  label,
  onClick,
  primary,
  disabled,
  tooltip,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  tooltip?: string;
}) {
  const button = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick();
      }}
      aria-disabled={disabled || undefined}
      className={`w-full rounded-[8px] border py-[7px] text-[12.5px] font-semibold transition-colors ${
        disabled
          ? 'cursor-not-allowed border-line bg-white text-ink-300'
          : primary
            ? 'border-brand-600 bg-brand-600 text-white hover:border-brand-700 hover:bg-brand-700'
            : 'border-line bg-white text-ink-700 hover:bg-surface-hover hover:text-ink-900'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-w-0 flex-1">
      {disabled && tooltip ? (
        <PortalTooltip content={tooltip} placement="top" align="center">
          {button}
        </PortalTooltip>
      ) : (
        button
      )}
    </div>
  );
}

interface InspectionCardProps {
  insp: InspectionListItem;
  onDeleteInspection: (id: string) => Promise<void>;
  onStartScheduled?: (id: string) => void;
  onCreateReport?: (id: string) => void;
}

function InspectionCard({ insp, onDeleteInspection, onStartScheduled, onCreateReport }: InspectionCardProps) {
  const navigate = useNavigate();
  const { canManageInspections, isOwner: isOrgOwner } = usePermissions(insp.siteId);
  const { currentMembership, user } = useAuthStore();
  const [actionOpen, setActionOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isActive = insp.status === InspectionStatus.IN_PROGRESS;
  const isDone = insp.status === InspectionStatus.COMPLETED || insp.status === InspectionStatus.PENDING_REVIEW;

  const scheduledStr = formatTime(insp.scheduledAt ?? undefined);
  const startedStr = (isActive || isDone) ? formatTime(insp.startedAt ?? undefined) : '';
  const completedStr = isDone ? formatTime(insp.completedAt ?? undefined) : '';

  const barColor = STATUS_BAR_COLOR[insp.status] ?? 'bg-ink-400';
  const statusLabel = STATUS_LABEL[insp.status] ?? insp.status;
  const statusTone = STATUS_BADGE_TONE[insp.status] ?? 'slate';
  const photoCount = insp.photoCount ?? insp._count?.photos ?? 0;

  const reportBadges = useMemo(() => {
    const reports = insp.reportInspections;
    if (!reports || reports.length === 0) return null;

    // 갈래는 「이 문서가 TBM 교육 흐름을 도는가」로 가른다 — 흐름 선언(정체 `type`이 아니다).
    // `type === 'TBM'`으로 물으면 Composer 전환본이 아래 일반 배지로 떨어져, TBM 회차·참석자
    // 대신 `COMPOSED:…`가 찍힌다.
    const tbmReports = reports.filter((ri) => isTbmEducationDocument(ri.report));
    const otherReports = reports.filter((ri) => !isTbmEducationDocument(ri.report));

    const badges: Array<{ label: string; tone: BadgeTone; key: string }> = [];

    for (const ri of otherReports) {
      // ⚠️ 라벨은 손맵이 아니라 정본을 부른다. 종전 `MAP[x] ?? x` 폴백이 화면에 **코드값**을
      // 그대로 뱉었다(`COMPOSED:작성중`) — `getReportTypeLabel`의 JSDoc이 2026-07-27에 결재함·
      // 문서관리에서 닫은 결함과 같은 것이고, 이 화면이 자기 맵을 따로 들고 있어 되살아났다.
      // 유형 라벨의 정본은 양식 이름(`templateName`)이지만 이 화면 응답엔 그 필드가 없어
      // 중립 폴백으로 나간다(응답 계약 확장은 별건 — 저장용 `type` 축은 없다).
      const typeLabel = getReportTypeLabel();
      // 상태는 데이터 판정 포함 정본 → 판정이 없을 때만 간결체(위 `compactStatusLabel` doc).
      // ⚠️ 이 화면 응답에는 판정 재료(postSubmitFlowKey·templateSnapshot·tbmSignatureMode·
      // approvalSignatures)가 실리지 않아 지금은 늘 종전 라벨로 떨어진다 — 안전한 폴백이고,
      // 재료가 실리는 날 이 자리는 저절로 옳아진다.
      const statusLabel = compactStatusLabel(
        ri.report.status,
        resolveReportStatusLabel(ri.report),
      );
      const tone = REPORT_STATUS_BADGE_TONE[ri.report.status] ?? 'slate';
      badges.push({ label: `${typeLabel}:${statusLabel}`, tone, key: ri.report.id });
    }

    for (const ri of tbmReports) {
      const totalSessions = ri.report.totalSessions ?? 0;
      const completedSessions = ri.report.completedSessions ?? 0;
      const attendeeCount = ri.report.attendeeCount ?? 0;
      // 작업 유형(공종)은 `splitGroupLabel` 컬럼이 정본이다(Composer 전환본에는 구 `content.workType`
      // 키가 없다). 응답도 그 스칼라 하나만 싣는다 — content 키에서 읽지 말 것.
      const workType = ri.report.splitGroupLabel || '';
      const tbmName = workType ? `TBM ${workType}` : 'TBM';

      let tbmLabel: string;
      if (totalSessions === 0) {
        tbmLabel = tbmName;
      } else if (totalSessions === 1) {
        tbmLabel = `${tbmName} (${attendeeCount}명)`;
      } else {
        tbmLabel = `${tbmName} (${completedSessions}/${totalSessions}회 · ${attendeeCount}명)`;
      }
      badges.push({
        label: tbmLabel,
        tone: 'blue',
        key: ri.report.id,
      });
    }

    return badges.length > 0 ? badges : null;
  }, [insp.reportInspections]);

  const { primaryName, secondaryNames } = useMemo(() => {
    // 활성 참여자만 (leftAt/excludedAt 없음)
    const activeCollaborators = (insp.collaborators ?? []).filter(
      (c) => !c.leftAt && !c.excludedAt,
    );
    if (activeCollaborators.length > 0) {
      const sorted = [...activeCollaborators].sort((a, b) => {
        if (a.role === 'PRIMARY' && b.role !== 'PRIMARY') return -1;
        if (a.role !== 'PRIMARY' && b.role === 'PRIMARY') return 1;
        return (a.joinedAt ?? '').localeCompare(b.joinedAt ?? '');
      });
      const primary = sorted.find((p) => p.role === 'PRIMARY') ?? sorted[0];
      const others = sorted.filter((p) => p !== primary);
      const formatName = (p: typeof sorted[0]) =>
        getEvidenceDisplayName(p.signerName ?? p.joinSnapshotName, { name: p.userName || p.user?.member?.name, deletedAt: p.user?.deletedAt }, '-');
      return {
        primaryName: formatName(primary),
        secondaryNames: others.map(formatName),
      };
    }
    return { primaryName: getEvidenceDisplayName(null, { name: insp.inspector?.member?.name, deletedAt: insp.inspector?.deletedAt }, '-'), secondaryNames: [] as string[] };
  }, [insp.collaborators, insp.inspector]);

  const handleDeleteClick = useCallback(() => {
    setActionOpen(false);
    setConfirmOpen(true);
  }, []);

  /**
   * 액션 활성/비활성과 그 사유 — 모달 목록과 sm+ 인라인 버튼 행이 같은 값을 읽는다.
   * 두 곳이 각자 계산하면 한쪽만 고쳤을 때 같은 카드가 서로 다른 가능 여부를 말하게 된다.
   */
  const actionState = useMemo(() => {
    const isScheduled = insp.status === InspectionStatus.SCHEDULED;
    const isCompleted = insp.status === InspectionStatus.COMPLETED;
    const isInProgress = insp.status === InspectionStatus.IN_PROGRESS;

    /**
     * 보고서 생성 = **내 권한 ∧ 서버 게이트**.
     *
     * ⚠️ 2026-10-09까지 이 자리는 `isCompleted` **하나뿐**이었다 — 권한도, 개선대책 선택도,
     * 점검완료 서명도 보지 않았다. 그래서 권한 없는 사람에게도 메뉴가 활성으로 보이고(서버가
     * 생성 시 403으로 막으므로 **거짓 어포던스**), 선택이 0인 점검도 열렸다(상세에서는 막힌다).
     *
     * ⚠️⚠️ **이 화면의 `reportGate`는 오늘 «항상 `undefined`»다 — 그래서 폴백이 산 코드다.**
     * 스케줄은 점검 목록 API가 아니라 **`GET /organizations/:id/inspection-schedules`**를 쓰고
     * (`organizations.service.findInspectionSchedules`), 그 쿼리의 `select`엔 게이트 재료
     * (`findings`·`photos`·`_count`·`collaborators.completionStatus`)가 **없다**. 즉 이 화면은
     * 아래 `?? isCompleted` 가지로만 돈다 ⇒ **선택·서명 축을 여기서는 아직 못 본다**(알려진
     * 한계). 짝 안전망은 서버 생성 경로의 선택 확인이고, 사용자가 상세로 들어가면 정본 게이트를
     * 본다. 그래도 **권한이 들어온 것만으로 오늘보다 엄격하다.**
     * 🔖 되짚을 조건: 그 쿼리가 게이트 재료를 싣게 되면 폴백이 죽은 가지가 된다 — 그때 지우고
     *    이 주석도 걷을 것. 그 전까지 「목록과 같다」로 적지 말 것(답이 갈린다).
     *
     * ⚠️ 폴백은 **「막는 쪽」이 아니라 종전 판정**이다. 없는 필드로 잠그면 이 화면의 「보고서
     * 생성」이 통째로 꺼진다.
     *
     * 사유는 **권한이 먼저**다 — 권한은 사용자가 바꿀 수 없으므로, 그보다 선택·서명을 먼저
     * 가리키면 **할 수 없는 일**을 안내하게 된다.
     *
     * ## ⚠️⚠️ 이 AND는 2026-10-09에 **일부러 남겼다 — 목록에서는 같은 날 걷었다**
     *
     * 그날 서버가 `canCreateReport`를 집행 식으로 계산해 `reportGate`에 싣게 됐고(`a8061c0c4`),
     * 그래서 **점검 목록**(`findAll`을 쓰는 행 메뉴)의 권한 AND는 중복이 되어 제거했다 — 그 AND가
     * 집행보다 좁아 prod **46건**을 거짓 차단하고 있었다(결정
     * `2026-10-09-the-enforcement-expression-is-the-canonical-gate.md`).
     *
     * **이 화면은 다르다.** 위 ⚠️⚠️대로 `reportGate`가 **항상 `undefined`**이므로 서버가 계산한
     * 권한이 **여기엔 도달하지 않는다.** 그래서 이 AND를 걷으면 `canCreateReport`가
     * `isCompleted` 하나로 떨어져 **권한 없는 사람에게 메뉴가 다시 활성**이 된다(= 그날 고친
     * 거짓 어포던스의 복구). 즉 여기서는 **중복이 아니라 유일한 권한 판정**이다.
     *
     * ⚠️ 그래서 이 화면의 답은 목록보다 **좁다**(같은 46건을 여기서는 여전히 막는다 — 알려진
     * 비대칭이고, 사용자가 상세·목록으로 가면 정본 판정을 본다). 「목록과 같다」로 적지 말 것.
     * 🔖 되짚을 조건은 위와 **하나**다 — `findInspectionSchedules`가 게이트를 싣게 되면 그때
     *    폴백과 **이 AND를 함께** 걷는다(먼저 걷으면 그 사이 문이 열린다).
     *    탐지기: `lib/__tests__/reportGate.test.ts` ⑫(분모가 이 파일 하나로 줄었다).
     */
    const hasReportPermission = canModifyInspectionRow({
      membership: currentMembership,
      isOwner: isOrgOwner || canManageInspections,
      currentUserId: user?.id,
      inspectorId: insp.inspectorId,
      siteId: insp.siteId,
    });
    const reportGateOpen = insp.reportGate?.canGenerate ?? isCompleted;
    const canCreateReport = hasReportPermission && reportGateOpen;
    let reportDisabledReason = '';
    if (!hasReportPermission) {
      reportDisabledReason = '보고서 생성은 주 담당자 또는 조직 관리자만 가능합니다.';
    } else if (insp.reportGate) {
      // 서버가 사유를 소유한다(`canGenerate === false ⟺ disabledReason !== null`).
      reportDisabledReason = insp.reportGate.disabledReason ?? '';
    } else if (!isCompleted) {
      reportDisabledReason = '점검 완료 시 활성화됩니다.';
    }

    // 삭제: SCHEDULED, IN_PROGRESS 활성 / COMPLETED 비활성 / 권한 필요
    const canDelete = (isScheduled || isInProgress) && canManageInspections;
    const deleteDisabledReason = !canManageInspections
      ? '점검 관리 권한이 없습니다.'
      : !canDelete ? '완료된 점검은 삭제할 수 없습니다.' : '';

    // 점검 시작: SCHEDULED일 때만, canStart는 서버에서 계산 (권한 무관 — 배정된 점검 수행)
    const canStart = isScheduled && !!insp.canStart;
    let startDisabledReason = '';
    if (isScheduled && !canStart && insp.scheduledAt) {
      const kstToday = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
      kstToday.setHours(0, 0, 0, 0);
      const scheduledKst = new Date(new Date(insp.scheduledAt).toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
      scheduledKst.setHours(0, 0, 0, 0);
      startDisabledReason = scheduledKst.getTime() > kstToday.getTime()
        ? '예약일 이후에 활성화됩니다.'
        : '이전 점검이 완료되어야 시작할 수 있습니다.';
    }

    return {
      isScheduled, isCompleted, isInProgress,
      canCreateReport, reportDisabledReason,
      canDelete, deleteDisabledReason,
      canStart, startDisabledReason,
    };
  }, [
    insp.status,
    insp.canStart,
    insp.scheduledAt,
    insp.reportGate,
    insp.inspectorId,
    insp.siteId,
    canManageInspections,
    isOrgOwner,
    currentMembership,
    user?.id,
  ]);

  const actionItems = useMemo(() => {
    const { isScheduled, canStart, startDisabledReason, canCreateReport, reportDisabledReason, canDelete, deleteDisabledReason } = actionState;

    const items: Array<{ label: string; subLabel?: string; onClick: () => void; danger?: boolean; disabled?: boolean; tooltip?: string; isLink?: boolean }> = [];

    if (isScheduled) {
      items.push({
        label: '점검 시작',
        onClick: () => { setActionOpen(false); onStartScheduled?.(insp.id); },
        disabled: !canStart,
        tooltip: startDisabledReason,
      });

      // 이전 차단 점검으로 이동 링크
      if (!canStart && insp.blockingInspectionId) {
        const orderText = insp.blockingInspectionOrder ? `${insp.blockingInspectionOrder}차 ` : '';
        items.push({
          label: `이전 ${orderText}점검을 완료하거나 삭제해야 합니다.`,
          subLabel: '이전 점검 보기 →',
          onClick: () => { setActionOpen(false); navigate(`/inspections/${insp.blockingInspectionId}`); },
          isLink: true,
        });
      }
    }

    // 상세 보기
    items.push({
      label: '상세 보기',
      onClick: () => { setActionOpen(false); navigate(`/inspections/${insp.id}`); },
    });

    items.push(
      {
        label: '보고서 생성',
        onClick: () => { setActionOpen(false); onCreateReport?.(insp.id); },
        disabled: !canCreateReport,
        tooltip: reportDisabledReason,
      },
      {
        label: '삭제',
        onClick: handleDeleteClick,
        danger: true,
        disabled: !canDelete,
        tooltip: deleteDisabledReason,
      },
    );

    return items;
  }, [actionState, insp.id, insp.blockingInspectionId, insp.blockingInspectionOrder, onStartScheduled, onCreateReport, navigate, handleDeleteClick]);

  /**
   * sm+ 카드 안 인라인 액션 — 모바일은 카드 탭 → 액션 모달을 그대로 쓴다(좁은 화면에서
   * 버튼 세 개를 나란히 두면 탭 표적이 무너진다). 삭제는 인라인에 없다: 되돌릴 수 없는
   * 동작이라 확인 단계가 있는 모달에만 둔다.
   */
  const inlineActions = useMemo(() => {
    const { isScheduled, isCompleted, canStart, canCreateReport, reportDisabledReason, startDisabledReason } = actionState;
    const list: Array<{ key: string; label: string; onClick: () => void; primary?: boolean; disabled?: boolean; tooltip?: string }> = [];

    if (isScheduled) {
      list.push({
        key: 'start',
        label: '점검 시작',
        onClick: () => onStartScheduled?.(insp.id),
        disabled: !canStart,
        tooltip: startDisabledReason,
      });
    }

    if (isCompleted || insp.status === InspectionStatus.PENDING_REVIEW) {
      list.push({
        key: 'report',
        label: '보고서',
        onClick: () => onCreateReport?.(insp.id),
        disabled: !canCreateReport,
        tooltip: reportDisabledReason,
      });
    }

    list.push({
      key: 'detail',
      label: '상세 보기',
      primary: true,
      onClick: () => navigate(`/inspections/${insp.id}`),
    });

    return list;
  }, [actionState, insp.id, insp.status, onStartScheduled, onCreateReport, navigate]);

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onDeleteInspection(insp.id);
    } finally {
      setIsDeleting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <div
        className="flex items-stretch gap-0 rounded-card border border-line bg-white overflow-hidden hover:border-brand-100 hover:shadow-card transition-all cursor-pointer"
        onClick={() => setActionOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setActionOpen(true)}
        aria-label={`${insp.site?.name ?? ''} 점검 액션 메뉴`}
      >
        {/* 좌측 상태 색상 바 */}
        <div className={`w-[3px] flex-shrink-0 ${barColor}`} />

        {/* 내용 */}
        <div className="flex-1 min-w-0 px-3 py-2.5">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-bold tracking-tight text-ink-900 truncate">
              {insp.site?.name ?? '-'}{insp.sequenceNumber != null ? ` ${insp.sequenceNumber}차 점검` : ''}
            </p>
            {isActive ? (
              // 진행 중인 점검만 점이 숨쉰다 — 목업 `.evc.s-prog .evc-st .d`.
              // Badge의 `dot` prop은 점에 클래스를 받지 않아(프리미티브가 색·크기 소유)
              // 애니메이션이 필요한 이 한 상태만 children으로 직접 넣는다.
              <Badge tone={statusTone} shape="rounded" size="sm" className="flex-shrink-0">
                <span
                  aria-hidden="true"
                  className="h-[5px] w-[5px] flex-none rounded-full bg-blue-600 motion-safe:animate-breathe"
                />
                {statusLabel}
              </Badge>
            ) : (
              <Badge tone={statusTone} shape="rounded" size="sm" dot className="flex-shrink-0">
                {statusLabel}
              </Badge>
            )}
          </div>
          <p className="text-xs font-medium text-ink-600 mt-0.5 truncate">
            <span className="text-ink-400">주담당자:</span> {primaryName}
          </p>
          {(scheduledStr || startedStr || completedStr) && (
            <p className="text-xs font-medium tabular-nums text-ink-500 mt-0.5 truncate flex items-center gap-1.5 flex-wrap">
              {scheduledStr && <span><span className="text-ink-400">예정</span> {scheduledStr}</span>}
              {startedStr && (
                <>
                  <span className="text-ink-300">·</span>
                  <span><span className="text-ink-400">시작</span> {startedStr}</span>
                </>
              )}
              {completedStr && (
                <>
                  <span className="text-ink-300">·</span>
                  <span><span className="text-ink-400">종료</span> {completedStr}</span>
                </>
              )}
              {isActive && !completedStr && (
                <>
                  <span className="text-ink-300">·</span>
                  <span className="text-blue-500">진행 중</span>
                </>
              )}
            </p>
          )}
          {secondaryNames.length > 0 && (
            <p className="text-xs font-medium text-ink-500 mt-0.5 truncate">
              <span className="text-ink-400">협업:</span> {secondaryNames.join(', ')}
            </p>
          )}
          {photoCount > 0 && (
            <p className="text-xs font-medium text-ink-500 mt-0.5">사진 {photoCount}장</p>
          )}
          {reportBadges && (
            <div className="mt-1.5 flex flex-col sm:flex-row flex-wrap gap-1">
              {reportBadges.map((badge) => (
                <Badge key={badge.key} tone={badge.tone} size="sm">
                  {badge.label}
                </Badge>
              ))}
            </div>
          )}

          {/* 인라인 액션 (sm+) — 모바일은 카드 탭 → 액션 모달 유지 */}
          <div className="mt-2.5 hidden gap-1.5 border-t border-line-soft pt-2.5 sm:flex">
            {inlineActions.map((action) => (
              <InlineActionButton
                key={action.key}
                label={action.label}
                onClick={action.onClick}
                primary={action.primary}
                disabled={action.disabled}
                tooltip={action.tooltip}
              />
            ))}
          </div>
        </div>

        {/* 우측 화살표 (카드 클릭 유도) */}
        <div className="flex-shrink-0 flex items-center pr-2">
          <ChevronRightIcon className="h-4 w-4 text-ink-300" />
        </div>
      </div>

      {/*
        액션 모달 — base Modal이 오버레이·Esc·포커스 가둠/복원·스크롤 잠금을 소유한다.

        모양: 폰에서는 화면 아래에 «떠 있는» 카드(가장자리 16px 여백 — 바닥 밀착이 아니다),
        sm 이상은 가운데 카드다. 그래서 `MOBILE_BOTTOM_PANEL_CLASS`(바닥·좌우 밀착)를 쓰지 않는다 —
        그 클래스는 「폰에서 면적이 곧 기능」인 서명 패드의 이유이고 이 목록에는 해당하지 않는다.
        16px 여백은 base 오버레이의 `p-4`가 그대로 만들어 주므로 패널은 `self-end`(align-self —
        오버레이의 `items-center`와 다른 property라 덧칠이 아니다)만 더한다.
        ⚠️ 폭: 종전 `sm:max-w-xs`(320px)는 base `SIZE_CLASS`와 같은 property라 덧칠할 수 없어
        `size="sm"`(384px)을 쓴다. 실제 위치·폭은 jsdom이 못 잰다 — 캡처가 소유한다.

        헤더·목록·닫기 버튼은 종전 그대로 본문에 두고(✕·base 제목 미사용) `-mx-5 -my-4`로 base 본문
        여백을 상쇄한다 — 행이 패널 가장자리까지 닿던 종전 모양을 유지하려는 것이다.
        `role="menuitem"`은 e2e(`schedule-and-members.spec.ts`)가 그 역할로 찾으므로 보존한다.
      */}
      <Modal
        open={actionOpen}
        onClose={() => setActionOpen(false)}
        ariaLabel={`${insp.site?.name ?? '점검'} 작업 선택`}
        showCloseButton={false}
        size="sm"
        overlayZClassName={MODAL_LAYER.root}
        panelClassName="self-end sm:self-center"
      >
          <div className="-mx-5 -my-4">
            {/* 헤더 */}
            <div className="px-4 py-3 border-b border-line-soft">
              <p className="text-sm font-bold tracking-tight text-ink-900 truncate">
                {insp.site?.name ?? '-'}{insp.sequenceNumber != null ? ` ${insp.sequenceNumber}차 점검` : ''}
              </p>
              <p className="text-xs font-medium text-ink-500 mt-0.5">{statusLabel}</p>
            </div>
            {/* 액션 목록 */}
            <div className="py-1">
              {actionItems.map((item) => {
                const row = (
                  <div
                    key={item.label}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      item.disabled
                        ? 'text-ink-300 cursor-not-allowed'
                        : item.danger
                          ? 'text-rose-600 active:bg-rose-50 cursor-pointer'
                          : item.isLink
                            ? 'cursor-pointer active:bg-amber-50'
                            : 'text-ink-700 active:bg-surface-hover cursor-pointer'
                    }`}
                    onClick={item.disabled ? undefined : item.onClick}
                    role="menuitem"
                    aria-disabled={item.disabled}
                  >
                    {item.isLink ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-card px-3 py-2">
                        <p className="text-xs font-medium text-amber-700">{item.label}</p>
                        {item.subLabel && (
                          <p className="text-xs text-brand-600 font-bold mt-0.5">{item.subLabel}</p>
                        )}
                      </div>
                    ) : item.label}
                  </div>
                );
                if (item.disabled && item.tooltip) {
                  return (
                    <PortalTooltip key={item.label} content={item.tooltip} placement="top" align="center">
                      {row}
                    </PortalTooltip>
                  );
                }
                return row;
              })}
            </div>
            {/* 닫기 */}
            <div className="border-t border-line-soft">
              <button
                onClick={() => setActionOpen(false)}
                className="w-full px-4 py-3 text-sm font-medium text-ink-500 text-center active:bg-surface-hover"
              >
                닫기
              </button>
            </div>
          </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="점검 삭제"
        message="이 점검을 삭제하시겠습니까?"
        confirmText="삭제"
        cancelText="취소"
        confirmStyle="danger"
        isLoading={isDeleting}
      />
    </>
  );
}

// I1: STATUS_BG_COLOR와 STATUS_DOT_COLOR가 동일하므로 STATUS_BG_COLOR 하나로 통합
const STATUS_BG_COLOR: Record<string, string> = {
  [InspectionStatus.SCHEDULED]: 'bg-violet-500',
  [InspectionStatus.IN_PROGRESS]: 'bg-blue-500',
  [InspectionStatus.COMPLETED]: 'bg-emerald-500',
  [InspectionStatus.PENDING_REVIEW]: 'bg-amber-500',
};

/** 프로그레스 바 */
function ProgressBar({ percent, colorClass }: { percent: number; colorClass: string }) {
  return (
    <div className="w-full bg-line rounded-full h-2 overflow-hidden">
      <div
        className={`h-2 rounded-full transition-all ${colorClass}`}
        style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </div>
  );
}

/** 가로 막대 (현장별/담당자별) */
function BarRow({ label, count, maxCount }: { label: string; count: number; maxCount: number }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-ink-700 w-24 truncate flex-shrink-0">{label}</span>
      <div className="flex-1 bg-surface-hover rounded-full h-2 overflow-hidden">
        <div
          className="h-2 bg-brand-500 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-semibold tabular-nums text-ink-500 w-8 text-right flex-shrink-0">{count}건</span>
    </div>
  );
}

/**
 * 월 요약 — 통계 카드.
 *
 * `monthPrefix`(YYYY-MM)로 먼저 걸러낸다: 주간 뷰는 앞뒤 달까지 프리페치하므로 받은 목록에
 * 다른 달 점검이 섞여 있어, 거르지 않으면 "6월 점검 요약"이 5·7월까지 합산한 수를 말한다.
 * 날짜 축은 범례 바 통계(`lib/monthStats.ts`의 `buildMonthStats`)와 같은 `scheduledAt ?? createdAt`
 * 이어야 한다 — 한쪽만 바꾸면 같은 화면의 두 숫자가 갈린다.
 */
function MonthSummary({
  inspections: allInspections,
  monthPrefix,
}: {
  inspections: InspectionListItem[];
  monthPrefix: string;
}) {
  const inspections = useMemo(
    () => filterInspectionsToMonth(allInspections, monthPrefix),
    [allInspections, monthPrefix],
  );

  const stats = useMemo(() => {
    const total = inspections.length;

    // W5: 4중 루프를 단일 루프로 통합
    // W3: 복수 점검에 연결된 동일 보고서가 여러 번 카운트되지 않도록 Set dedup
    const statusCounts: Record<string, number> = {};
    const inspectorMap = new Map<string, number>();
    // 유형별 집계를 두지 않는다 — 저장용 `type` 축이 없어 모든 문서가 한 버킷이고, 그러면 총 문서
    // 수와 같은 숫자를 한 번 더 그릴 뿐이다. 양식별로 나누려면 이 응답에 `templateKey`/`templateName`을
    // 실어야 한다(응답 계약 확장은 별건).
    let totalReports = 0;
    const reportAuthorMap = new Map<string, number>();
    const seenReportIds = new Set<string>();
    let approvedReports = 0;

    for (const insp of inspections) {
      // 상태별 카운트
      statusCounts[insp.status] = (statusCounts[insp.status] ?? 0) + 1;

      // 담당자별 점검 카운트: PRIMARY collaborator 스냅샷 우선, 없으면 inspector 라이브 fallback
      const statsActiveCollabs = (insp.collaborators ?? []).filter((c) => !c.leftAt && !c.excludedAt);
      const statsPrimary = statsActiveCollabs.find((c) => c.role === 'PRIMARY') ?? statsActiveCollabs[0] ?? null;
      const name = getEvidenceDisplayName(
        statsPrimary?.signerName ?? statsPrimary?.joinSnapshotName ?? null,
        { name: insp.inspector?.member?.name, deletedAt: insp.inspector?.deletedAt },
        '알 수 없음',
      );
      inspectorMap.set(name, (inspectorMap.get(name) ?? 0) + 1);

      // 보고서 순회 (중복 제거)
      if (insp.reportInspections) {
        let newReportCount = 0;
        for (const ri of insp.reportInspections) {
          if (seenReportIds.has(ri.report.id)) continue;
          seenReportIds.add(ri.report.id);
          newReportCount++;
          totalReports++;
          if (ri.report.status === 'APPROVED') approvedReports++;
        }
        if (newReportCount > 0) {
          reportAuthorMap.set(name, (reportAuthorMap.get(name) ?? 0) + newReportCount);
        }
      }
    }

    const completed = statusCounts[InspectionStatus.COMPLETED] ?? 0;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    const inspectorRanking = Array.from(inspectorMap.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    const approvalRate = totalReports > 0 ? Math.round((approvedReports / totalReports) * 100) : 0;

    const reportAuthorRanking = Array.from(reportAuthorMap.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return {
      total, statusCounts, completed, completionRate, inspectorRanking,
      totalReports, approvedReports, approvalRate, reportAuthorRanking,
    };
  }, [inspections]);

  if (stats.total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4">
        <svg className="w-10 h-10 text-ink-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-sm font-medium text-ink-500">이번 달 예정된 점검이 없습니다.</p>
      </div>
    );
  }

  const statusOrder = [
    InspectionStatus.SCHEDULED,
    InspectionStatus.IN_PROGRESS,
    InspectionStatus.COMPLETED,
    InspectionStatus.PENDING_REVIEW,
  ];
  const inspectorMax = stats.inspectorRanking[0]?.[1] ?? 1;

  const reportAuthorMax = stats.reportAuthorRanking[0]?.[1] ?? 1;

  return (
    <div className="overflow-y-auto max-h-[calc(100vh-200px)] py-3 px-3">
      <div className="min-[680px]:grid min-[680px]:grid-cols-2 min-[680px]:gap-4 flex flex-col gap-3">

        {/* 좌: 점검 섹션 */}
        <div className="flex flex-col gap-3">
          {/* 총 점검 + 상태별 */}
          <Card surface="soft" elevation="none" padding="none" className="px-4 py-3 flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-semibold text-ink-500">총 점검</span>
              <span className="text-lg font-extrabold tracking-tight tabular-nums text-ink-900">{stats.total}건</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {statusOrder.map((s) => {
                const cnt = stats.statusCounts[s];
                if (!cnt) return null;
                return (
                  <span key={s} className="flex items-center gap-1 text-xs font-medium text-ink-600">
                    <span className={`inline-block w-2 h-2 rounded-full ${STATUS_BG_COLOR[s] ?? 'bg-ink-400'}`} />
                    {STATUS_LABEL[s] ?? s} {cnt}
                  </span>
                );
              })}
            </div>
          </Card>

          {/* 점검률 */}
          <Card surface="soft" elevation="none" padding="none" className="px-4 py-3 flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-semibold text-ink-500">점검률</span>
              <span className="text-sm font-extrabold tabular-nums text-emerald-600">{stats.completionRate}%</span>
            </div>
            <ProgressBar percent={stats.completionRate} colorClass={STATUS_BG_COLOR[InspectionStatus.COMPLETED]} />
            <p className="text-xs font-medium tabular-nums text-ink-400">완료 {stats.completed} / 전체 {stats.total}</p>
          </Card>

          {/* 담당자별 점검 수 */}
          {stats.inspectorRanking.length > 0 && (
            <Card surface="soft" elevation="none" padding="none" className="px-4 py-3 flex flex-col gap-2">
              <span className="text-xs font-bold text-ink-600">담당자별</span>
              <div className="flex flex-col gap-1.5">
                {stats.inspectorRanking.map(([name, cnt]) => (
                  <BarRow key={name} label={name} count={cnt} maxCount={inspectorMax} />
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* 우: 생성 문서 섹션 */}
        {stats.totalReports > 0 && (
          <div className="flex flex-col gap-3">
            {/* 모바일 전용 구분선 */}
            <div className="flex items-center gap-2 min-[680px]:hidden">
              <div className="flex-1 border-t border-line" />
              <span className="text-[10px] text-ink-400 font-semibold">생성 문서</span>
              <div className="flex-1 border-t border-line" />
            </div>

            {/* 총 문서 (유형별 내역은 `type` 축 삭제로 걷음 — 위 stats 주석) */}
            <Card surface="soft" elevation="none" padding="none" className="px-4 py-3 flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-semibold text-ink-500">총 문서</span>
                <span className="text-lg font-extrabold tracking-tight tabular-nums text-ink-900">{stats.totalReports}건</span>
              </div>
            </Card>

            {/* 결재완료율 */}
            <Card surface="soft" elevation="none" padding="none" className="px-4 py-3 flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-semibold text-ink-500">결재완료율</span>
                <span className="text-sm font-extrabold tabular-nums text-brand-600">{stats.approvalRate}%</span>
              </div>
              <ProgressBar percent={stats.approvalRate} colorClass="bg-brand-600" />
              <p className="text-xs font-medium tabular-nums text-ink-400">완료 {stats.approvedReports} / 전체 {stats.totalReports}</p>
            </Card>

            {/* 담당자별 문서 생성 수 */}
            {stats.reportAuthorRanking.length > 0 && (
              <Card surface="soft" elevation="none" padding="none" className="px-4 py-3 flex flex-col gap-2">
                <span className="text-xs font-bold text-ink-600">담당자별 문서</span>
                <div className="flex flex-col gap-1.5">
                  {stats.reportAuthorRanking.map(([name, cnt]) => (
                    <BarRow key={name} label={name} count={cnt} maxCount={reportAuthorMax} />
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default function DayInspectionList({
  date,
  inspections,
  allInspections = [],
  currentMonth,
  onCreateInspection,
  onDeleteInspection,
  onStartScheduled,
  onCreateReport,
}: DayInspectionListProps) {
  const displayYear = currentMonth?.year ?? new Date().getFullYear();
  const displayMonth = currentMonth?.month ?? new Date().getMonth(); // 0-indexed
  // "새 점검 예약" 버튼은 항상 표시 — 모달 내 현장 선택 후 권한 체크

  // 날짜 미선택 → 이번 달 요약
  if (!date) {
    // 제목이 말하는 달과 합산 대상이 같아야 한다(제목은 `displayMonth`, 요약은 이 접두사).
    const monthPrefix = `${displayYear}-${String(displayMonth + 1).padStart(2, '0')}`;
    return (
      <Card padding="none" radius="lg" className="flex flex-col overflow-hidden">
        <div className="border-b border-line-soft bg-gradient-to-b from-brand-50 to-white px-4 py-3.5">
          <h3 className="text-[15px] font-extrabold tracking-tight text-ink-900">{displayMonth + 1}월 점검 요약</h3>
        </div>
        <MonthSummary inspections={allInspections} monthPrefix={monthPrefix} />
      </Card>
    );
  }

  return (
    <Card padding="none" radius="lg" className="flex flex-col overflow-hidden">
      {/* 헤더 */}
      <div className="flex items-start justify-between gap-2 border-b border-line-soft bg-gradient-to-b from-brand-50 to-white px-4 py-3.5">
        <div className="min-w-0">
          <h3 className="text-[20px] font-extrabold leading-tight tracking-tight text-ink-900">
            {formatDayLabel(date)}
          </h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-[13px] font-medium text-ink-500">
            {formatWeekdayLabel(date)}
            {/* 건수 칩 — 목업 `.sp-h .d2 .cnt`. 상태가 섞여 있을 수 있어 "예정"이 아니라
                "점검"으로 센다(예약·진행·완료가 같은 날에 함께 놓인다). */}
            {inspections.length > 0 && (
              <Badge tone="brand" shape="rounded" size="sm">
                점검 {inspections.length}건
              </Badge>
            )}
          </p>
        </div>
        <button
          onClick={() => onCreateInspection(date)}
          className="flex flex-none items-center gap-1 rounded-card-sm border border-brand-100 bg-white px-2.5 py-1.5 text-xs font-bold text-brand-700 hover:bg-brand-50 hover:border-brand-500 transition-colors"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          새 점검 예약
        </button>
      </div>

      {/* 목록 */}
      <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
        {inspections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4">
            <div className="mb-3 grid h-16 w-16 place-items-center rounded-card-lg border border-line bg-gradient-to-br from-surface-soft to-white text-ink-300">
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-ink-600">이 날 예정된 점검이 없습니다.</p>
            <button
                onClick={() => onCreateInspection(date)}
                className="mt-3 rounded-card-sm border border-brand-100 bg-white px-4 py-2 text-xs font-bold text-brand-700 transition-colors hover:border-brand-500 hover:bg-brand-50"
              >
                점검 예약하기
              </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 p-3">
            {inspections.map((insp) => (
              <InspectionCard
                key={insp.id}
                insp={insp}
                onDeleteInspection={onDeleteInspection}
                onStartScheduled={onStartScheduled}
                onCreateReport={onCreateReport}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
